import { useEffect, useMemo } from 'react'
import { fetchExperienceById, fetchExperienceDates, fetchTransferOptions } from 'actions/ExperienceActions'
import { useAppDispatch, useAppSelector, shallowEqual } from 'hooks/reduxHooks'
import { mapExperienceToBookingView, getExperienceDatesKey } from 'lib/experiences/experienceUtils'
import { EmptyArray } from 'lib/array/arrayUtils'
import { hasConfiguredHomeToAirportOption } from 'loyalty/lib/transferOfferUtils'
import { findRedemptionLocationByAirportId } from 'lib/experiences/redemptionLocationUtils'

interface HomeToAirportTransferData {
  fetchingExperience: boolean
  fetchingDates: boolean
  fetchingTransferOptions: boolean
  redemptionLocations: Array<App.ExperienceRedemptionLocation>
  selectedRedemptionLocation?: App.ExperienceRedemptionLocation
  availableDates: Array<App.ExperienceAvailabilityDate>
  transferOptions: Array<App.ExperienceTransferOption>
  experienceBookingView?: App.ExperienceBookingView
}

/**
 * Hook to fetch transfer experience data for home-to-airport loyalty transfers.
 *
 * Fetches the loyalty magic experience and its dates. Transfer options use the same
 * fetch as the experience offer form (fetchTransferOptions by experienceId); options
 * are stable across location/date for this experience.
 *
 * @param selectedAirportId - The ID of the airport/redemption location selected by the customer
 * @param selectedDate - The date selected by the customer (YYYY-MM-DD format)
 */
export default function useHomeToAirportTransferData(
  selectedAirportId?: string,
  _selectedDate?: string,
): HomeToAirportTransferData {
  const dispatch = useAppDispatch()
  const loyaltyConfig = useAppSelector(state => state.loyalty.config)
  const transferAirportCodes = loyaltyConfig?.transferAirports ?? EmptyArray
  const geoAirports = useAppSelector(state => state.geo.airports)
  const experienceId = hasConfiguredHomeToAirportOption(loyaltyConfig) ?
    loyaltyConfig?.transferOffers?.loyaltyHomeToAirport?.experienceId :
    undefined
  const {
    experience,
    fetchingExperience,
    experienceDates,
    transferOptions,
    fetchingTransferOptions,
  } = useAppSelector((state) => {
    if (!experienceId) {
      return {
        experience: undefined,
        fetchingExperience: false,
        experienceDates: undefined,
        transferOptions: EmptyArray,
        fetchingTransferOptions: false,
      }
    }
    const transferState = state.experience.experienceTransferOptions[experienceId]
    return {
      experience: state.experience.experiences[experienceId],
      fetchingExperience: state.experience.fetchingExperiences[experienceId] ?? false,
      experienceDates: state.experience.experienceDates[experienceId],
      transferOptions: transferState?.transfers ?? EmptyArray,
      fetchingTransferOptions: transferState?.fetching ?? false,
    }
  }, shallowEqual)

  useEffect(() => {
    if (experienceId) {
      dispatch(fetchExperienceById(experienceId))
    }
  }, [dispatch, experienceId])

  useEffect(() => {
    if (experienceId) {
      dispatch(fetchTransferOptions(experienceId))
    }
  }, [dispatch, experienceId])

  const fallbackRedemptionLocations = useMemo((): Array<App.ExperienceRedemptionLocation> => {
    if (fetchingExperience || experience?.redemptionLocations?.length) return EmptyArray

    return transferAirportCodes.reduce<Array<App.ExperienceRedemptionLocation>>((locations, airportCode) => {
      const airport = geoAirports.find(({ code }) => code === airportCode)
      if (!airport) return locations

      locations.push({
        id: airport.code,
        name: airport.name,
        latitude: airport.latitude,
        longitude: airport.longitude,
        countryCode: airport.countryCode ?? '',
        city: airport.name,
        state: airport.code,
      })

      return locations
    }, [])
  }, [fetchingExperience, experience?.redemptionLocations, transferAirportCodes, geoAirports])

  const redemptionLocations = experience?.redemptionLocations?.length
    ? experience.redemptionLocations
    : fallbackRedemptionLocations

  const selectedRedemptionLocation = useMemo(() => {
    return findRedemptionLocationByAirportId(redemptionLocations, selectedAirportId)
  }, [redemptionLocations, selectedAirportId])

  const datesKey = useMemo(() => {
    if (!experienceId || !experience || !selectedRedemptionLocation) return undefined
    return getExperienceDatesKey(experienceId, {
      redemptionLocationId: selectedRedemptionLocation.id,
    })
  }, [experienceId, experience, selectedRedemptionLocation])

  useEffect(() => {
    if (experienceId && experience && selectedRedemptionLocation && datesKey && !experienceDates?.[datesKey]) {
      dispatch(fetchExperienceDates(experienceId, {
        redemptionLocationId: selectedRedemptionLocation.id,
      }))
    }
  }, [dispatch, experience, selectedRedemptionLocation, datesKey, experienceDates, experienceId])

  const availableDates = datesKey ? (experienceDates?.[datesKey]?.dates ?? EmptyArray) : EmptyArray
  const fetchingDates = datesKey ? !!experienceDates?.[datesKey]?.fetching : false

  const experienceBookingView = useMemo((): App.ExperienceBookingView | undefined => {
    if (!experience) return undefined

    const bookingView = mapExperienceToBookingView(experience)

    // Override the airport with coordinates from the selected redemption location
    if (selectedRedemptionLocation && bookingView.originalExperience) {
      // Use city name as display, state as code (we don't have actual airport codes)
      const airport: App.Airport = {
        code: selectedRedemptionLocation.state ?? '',
        name: selectedRedemptionLocation.city ?? selectedRedemptionLocation.name,
        latitude: selectedRedemptionLocation.latitude,
        longitude: selectedRedemptionLocation.longitude,
      }
      return {
        ...bookingView,
        originalExperience: {
          ...bookingView.originalExperience,
          airport,
        },
      }
    }

    return bookingView
  }, [experience, selectedRedemptionLocation])

  return {
    fetchingExperience,
    fetchingDates,
    fetchingTransferOptions,
    redemptionLocations,
    selectedRedemptionLocation,
    availableDates,
    transferOptions,
    experienceBookingView,
  }
}
